import { z } from "zod";

import type { CNSWorkflowSnapshot } from "@/models/cns";
import { completedActionReceiptWireSchema } from "@/types/actionReceipt.schema";

const workflowStageSchema = z.enum([
  "DISCOVERY",
  "DRAFT",
  "TRACKER",
  "RUNNING",
  "COMPLETED",
  "INSIGHTS",
  "LEARNING",
]);

const experimentStatusSchema = z.enum([
  "DRAFT",
  "APPROVED",
  "RUNNING",
  "COMPLETED",
  "ARCHIVED",
]);

const hypothesisStatusSchema = z.enum(["DRAFT", "ACTIVE", "ARCHIVED"]);
const actionRequestSlotSchema = z.enum(["workflow", "source_connection"]);

const activeActionRequestSchema = z.discriminatedUnion("origin", [
  z
    .object({
      origin: z.literal("assistant_turn"),
      actionRequestId: z.string().trim().min(1),
      actionType: z.string().trim().min(1),
      slot: actionRequestSlotSchema,
      messagePublicId: z.string().trim().min(1),
      partId: z.string().trim().min(1),
    })
    .strict(),
  z
    .object({
      origin: z.literal("workflow_surface"),
      actionRequestId: z.string().trim().min(1),
      actionType: z.string().trim().min(1),
      slot: actionRequestSlotSchema,
      description: z.string().trim().min(1),
      payload: z.record(z.string(), z.unknown()),
    })
    .strict(),
]);

const completedWorkflowActionSchema = z
  .object({
    origin: z.literal("workflow_surface"),
    actionRequestId: z.string().trim().min(1),
    actionType: z.string().trim().min(1),
    slot: z.literal("workflow"),
    payload: z.record(z.string(), z.unknown()),
    receipt: completedActionReceiptWireSchema,
  })
  .strict();

/** ISO-8601 instant emitted by the backend for freshness ordering. */
export const workflowSnapshotComputedAtSchema = z.iso.datetime({
  offset: true,
});

/** Runtime wire contract shared by snapshot REST responses and conversation SSE. */
export const workflowSnapshotWireSchema = z
  .object({
    conversationPublicId: z.string().trim().min(1),
    projectPublicId: z.string().trim().min(1),
    primaryExperimentPublicId: z.string().nullable().optional(),
    primaryHypothesisPublicId: z.string().nullable().optional(),
    experimentStatus: experimentStatusSchema.nullable().optional(),
    hypothesisStatus: hypothesisStatusSchema.nullable().optional(),
    currentStage: workflowStageSchema,
    trackerPresent: z.boolean(),
    trackerLocked: z.boolean(),
    trackerRowCount: z.number().int().nonnegative(),
    primaryTrackerPublicId: z.string().nullable().optional(),
    latestLearningPublicId: z.string().nullable().optional(),
    synthesisLearningPublicId: z.string().nullable().optional(),
    evidenceSufficiencyRecorded: z.boolean(),
    evidenceSufficient: z.boolean(),
    permittedTransitionIds: z.array(z.string()),
    activeActionRequests: z.array(activeActionRequestSchema),
    latestCompletedWorkflowAction: completedWorkflowActionSchema
      .nullable()
      .optional(),
    computedAt: workflowSnapshotComputedAtSchema,
    primaryExperimentSelectionReason: z.string().nullable().optional(),
    insufficientEvidenceRationale: z.string().nullable().optional(),
  })
  .strict() satisfies z.ZodType<CNSWorkflowSnapshot>;

export function parseWorkflowSnapshotWire(input: unknown): CNSWorkflowSnapshot {
  const parsed = workflowSnapshotWireSchema.safeParse(input);
  if (!parsed.success) {
    throw new Error("Workflow state response was invalid.");
  }
  return parsed.data;
}
